/** Small geometry helpers the map and the pages share. */
import type { Feature, Geometry, Position } from 'geojson';

/** A point to draw a polygon's line from: the area centroid of its largest ring (the vertices' mean for a degenerate one). */
export function representativePoint(g: Geometry | null): [number, number] | null {
  if (!g) return null;
  if (g.type === 'Point') return [g.coordinates[0]!, g.coordinates[1]!];
  const rings = g.type === 'Polygon' ? [g.coordinates[0]] : g.type === 'MultiPolygon' ? g.coordinates.map((p) => p[0]) : [];
  let best: { a: number; x: number; y: number } | null = null;
  for (const ring of rings) {
    if (!ring?.length) continue;
    // Relative to the ring's first vertex: in degrees, products of absolute coordinates (≈ 14 × 50)
    // swamp the tiny area of a crown, and the centroid lands kilometres away.
    const [ox, oy] = [ring[0]![0]!, ring[0]![1]!];
    let a = 0;
    let cx = 0;
    let cy = 0;
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const [x0, y0] = [ring[j]![0]! - ox, ring[j]![1]! - oy];
      const [x1, y1] = [ring[i]![0]! - ox, ring[i]![1]! - oy];
      const f = x0 * y1 - x1 * y0;
      a += f;
      cx += (x0 + x1) * f;
      cy += (y0 + y1) * f;
    }
    const point =
      a === 0 ? { a: 0, x: ring.reduce((s, p) => s + p[0]!, 0) / ring.length, y: ring.reduce((s, p) => s + p[1]!, 0) / ring.length } : { a: Math.abs(a / 2), x: ox + cx / (3 * a), y: oy + cy / (3 * a) };
    if (!best || point.a > best.a) best = point;
  }
  return best ? [best.x, best.y] : null;
}


function inRing(x: number, y: number, ring: Position[]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i]!;
    const [xj, yj] = ring[j]!;
    if (yi! > y !== yj! > y && x < ((xj! - xi!) * (y - yi!)) / (yj! - yi!) + xi!) inside = !inside;
  }
  return inside;
}

/** Whether a point lies in a polygon or multipolygon, holes left out. */
export function inGeometry(x: number, y: number, g: Geometry | null): boolean {
  if (!g) return false;
  const polygons = g.type === 'Polygon' ? [g.coordinates] : g.type === 'MultiPolygon' ? g.coordinates : [];
  return polygons.some(([outer, ...holes]) => !!outer && inRing(x, y, outer) && !holes.some((h) => inRing(x, y, h)));
}

function bbox(g: Geometry | null): [number, number, number, number] | null {
  if (!g || !('coordinates' in g)) return null;
  let box: [number, number, number, number] | null = null;
  const visit = (c: unknown): void => {
    if (!Array.isArray(c)) return;
    if (typeof c[0] === 'number') {
      const [x, y] = c as [number, number];
      box = box ? [Math.min(box[0], x), Math.min(box[1], y), Math.max(box[2], x), Math.max(box[3], y)] : [x, y, x, y];
    } else for (const inner of c) visit(inner);
  };
  visit(g.coordinates);
  return box;
}

/** The first of `features` whose polygon holds the point. */
export function featureAt(features: Feature[], x: number, y: number): Feature | undefined {
  return features.find((f) => inGeometry(x, y, f.geometry));
}

/** A test for "inside any of the polygons", each polygon's box checked first, each ring banded. */
export function insideAny(polygons: Feature[]): (x: number, y: number) => boolean {
  const shapes = polygons.flatMap((f) => {
    const box = bbox(f.geometry);
    const g = f.geometry;
    if (!box || !g) return [];
    const parts = g.type === 'Polygon' ? [g.coordinates] : g.type === 'MultiPolygon' ? g.coordinates : [];
    return [{ box, parts: parts.map((rings) => rings.map(bandedRing)) }];
  });
  return (x, y) =>
    shapes.some(({ box, parts }) => x >= box[0] && x <= box[2] && y >= box[1] && y <= box[3] && parts.some(([outer, ...holes]) => !!outer && outer(x, y) && !holes.some((h) => h(x, y))));
}

/** Horizontal bands per ring: a point is tested against the few edges its band crosses, not the whole ring. */
const BANDS = 256;

/** A ring's even-odd test with its edges sorted into horizontal bands, so a unit of thousands of vertices tests a point in a handful of steps. */
function bandedRing(ring: Position[]): (x: number, y: number) => boolean {
  let lo = Infinity;
  let hi = -Infinity;
  for (const p of ring) [lo, hi] = [Math.min(lo, p[1]!), Math.max(hi, p[1]!)];
  const height = hi - lo || 1;
  const bands: number[][] = Array.from({ length: BANDS }, () => []);
  const band = (y: number) => Math.min(BANDS - 1, Math.max(0, Math.floor(((y - lo) / height) * BANDS)));
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [a, b] = [band(ring[i]![1]!), band(ring[j]![1]!)];
    for (let k = Math.min(a, b); k <= Math.max(a, b); k++) bands[k]!.push(i);
  }
  return (x, y) => {
    if (y < lo || y > hi) return false;
    let inside = false;
    for (const i of bands[band(y)]!) {
      const j = i === 0 ? ring.length - 1 : i - 1;
      const [xi, yi] = ring[i]!;
      const [xj, yj] = ring[j]!;
      if (yi! > y !== yj! > y && x < ((xj! - xi!) * (y - yi!)) / (yj! - yi!) + xi!) inside = !inside;
    }
    return inside;
  };
}

